import * as signalR from "@microsoft/signalr";

// EmailMessageStatus.OnboardingPipelineTest
const ONBOARDING_TEST_STATUS = "OnboardingPipelineTest";

const CONNECT_RETRY_DELAYS_MS = [0, 2000, 5000, 10000];

/** Min time between Connect-step pipeline-status checks (initial, visibility, or prior fetch completion). */
const CONNECT_PIPELINE_CHECK_MIN_INTERVAL_MS = 60 * 1000;

const PILL_COPY = {
    waiting: "Waiting for your first email.",
    verified: "Connection verified. Send a real message to start your digest.",
    confirmed: "Your first email is in. You're set.",
    stalled: "Still waiting. Check spam filters and confirm forwarding is enabled."
};

function eventStatus(evt) {
    if (!evt) return null;
    return evt.status || evt.Status || null;
}

// Mirrors GetOnboardingPipelineStatusAsync: non-test = any status except OnboardingPipelineTest.
function classifyPipelineKind(status) {
    if (!status) return null;
    if (status === ONBOARDING_TEST_STATUS) return "test";
    return "real";
}

function trackGtag(eventName) {
    if (typeof window.gtag !== "function") return;
    window.gtag("event", eventName, {
        page_path: window.location.pathname,
        page_title: document.title
    });
}

function renderPillState(pillEl, textEl, state) {
    if (!pillEl) return;
    pillEl.setAttribute("data-state", state);
    if (textEl) textEl.textContent = PILL_COPY[state] || PILL_COPY.waiting;
}

function deriveStateFromFlags(state) {
    if (state.realSeen) return "confirmed";
    if (state.testSeen) return "verified";
    return "waiting";
}

function nudgeContinueButton() {
    var btn = document.getElementById("ob-connect-continue-btn");
    if (!btn || btn.classList.contains("ob-btn--nudge")) return;
    btn.classList.add("ob-btn--nudge");
    setTimeout(function () {
        btn.classList.remove("ob-btn--nudge");
    }, 3600);
}

function mergePipelineFlagsFromJson(data, state, emitAnalytics) {
    if (!data || data.onboardingComplete || data.error) return;
    if (data.hasPipelineTestMessage && !state.testSeen) {
        state.testSeen = true;
        if (emitAnalytics) trackGtag("onboarding_pipeline_test_received");
    }
    if (data.hasAnyNonTestReceivedMessage && !state.realSeen) {
        state.realSeen = true;
        if (emitAnalytics) trackGtag("onboarding_real_mail_received");
    }
}

function fetchPipelineOnce() {
    return fetch("/welcome/pipeline-status", { credentials: "same-origin" }).then(function (res) {
        if (res.status === 429) return null;
        return res.ok ? res.json() : null;
    });
}

function startEmailProcessingHub(onStatusChanged) {
    var connection = new signalR.HubConnectionBuilder()
        .withUrl("/hubs/email-processing")
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

    connection.on("emailStatusChanged", onStatusChanged);

    function startAttempt(attempt) {
        connection.start().catch(function (err) {
            console.warn("Onboarding SignalR connection failed (attempt " + (attempt + 1) + "):", err);
            if (attempt < CONNECT_RETRY_DELAYS_MS.length - 1) {
                setTimeout(function () {
                    startAttempt(attempt + 1);
                }, CONNECT_RETRY_DELAYS_MS[attempt + 1]);
            }
        });
    }

    startAttempt(0);

    window.addEventListener("pagehide", function () {
        connection.stop();
    });

    return connection;
}

function initConnectPipelineHub() {
    var pillEl = document.getElementById("ob-pipeline-pill");
    var textEl = document.getElementById("ob-pipeline-status-text");
    if (!pillEl || !textEl) return;

    var state = { testSeen: false, realSeen: false };
    var lastConnectPipelineCheckAt = Date.now();

    function touchConnectPipelineCheckTime() {
        lastConnectPipelineCheckAt = Date.now();
    }

    function apply() {
        var next = deriveStateFromFlags(state);
        renderPillState(pillEl, textEl, next);
        if (next === "confirmed") {
            nudgeContinueButton();
        }
    }

    function applyFetchResult(data, emitAnalytics) {
        mergePipelineFlagsFromJson(data, state, emitAnalytics);
        apply();
    }

    function onHubEvent(evt) {
        var kind = classifyPipelineKind(eventStatus(evt));
        if (kind === "test" && !state.testSeen) {
            state.testSeen = true;
            trackGtag("onboarding_pipeline_test_received");
        } else if (kind === "real" && !state.realSeen) {
            state.realSeen = true;
            trackGtag("onboarding_real_mail_received");
        } else {
            return;
        }
        apply();
    }

    renderPillState(pillEl, textEl, "waiting");
    startEmailProcessingHub(onHubEvent);

    fetchPipelineOnce()
        .then(function (data) {
            applyFetchResult(data, true);
        })
        .finally(touchConnectPipelineCheckTime);

    function onVisibilityChange() {
        if (document.hidden) return;
        if (state.realSeen) return;
        if (Date.now() - lastConnectPipelineCheckAt < CONNECT_PIPELINE_CHECK_MIN_INTERVAL_MS) return;
        touchConnectPipelineCheckTime();
        fetchPipelineOnce()
            .then(function (data) {
                applyFetchResult(data, true);
            })
            .finally(touchConnectPipelineCheckTime);
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener(
        "pagehide",
        function () {
            document.removeEventListener("visibilitychange", onVisibilityChange);
        },
        { once: true }
    );

    var maxMs = 10 * 60 * 1000;
    setTimeout(function () {
        if (state.testSeen || state.realSeen) return;
        renderPillState(pillEl, textEl, "stalled");
    }, maxMs);
}

function init() {
    if (document.querySelector(".ob--connect") && document.getElementById("ob-pipeline-pill")) {
        initConnectPipelineHub();
    }
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
} else {
    init();
}
